import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SmsRounded from '@mui/icons-material/SmsRounded';
import PhoneMissedRounded from '@mui/icons-material/PhoneMissedRounded';
import { EmptyState, ListTable, MetricCard, Panel, StatusChip, type ListColumn, type MetricTrend } from '../../../ui-kit';
import {
  fetchMcSpotlight, watchMcCases, type McCaseCard, type McDay, type McSpotlight,
} from '../../../services/missedCallService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { fromNow, useLive } from '../rocco/live';
import CaseStoryModal from './CaseStory';
import { AgencyZoomMark, TwilioMark, WhatsAppMark } from './logos';
import { statusChip } from './status';

const whole = (v: number) => v.toLocaleString();

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: McSpotlight = {
  flow: { agency_number: null, team_phone: null, ring_seconds: 20, whatsapp_from: '' },
  totals: { calls: 0, taken_by_team: 0, missed: 0, replied: 0, booked: 0, needs_person: 0, stopped: 0, waiting: 0,
    median_minutes_to_book: null },
  missed_by_day: [],
  booked_by_day: [],
  latest: [],
};

/**
 * The page the Missed Call Follow-up agent opens on. Every figure is read
 * from the running system.
 *
 * Top to bottom: how a call moves through it, the numbers so far, then the
 * latest missed calls.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { data, error } = useLive(fetchMcSpotlight, 10_000, { watch: watchMcCases });
  const [open, setOpen] = React.useState<string | null>(null);

  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const { flow, totals } = data ?? EMPTY;

  const nodes: FlowNode[] = [
    {
      id: 'call', step: 'A client calls', name: 'Twilio',
      detail: flow.agency_number ? `Your dialer · ${flow.agency_number}` : 'Your dialer', logo: <TwilioMark />,
      to: 'integrations',
    },
    {
      id: 'missed', step: 'Nobody takes it', name: 'Missed call',
      detail: 'The caller hears our message', logo: <PhoneMissedRounded sx={{ fontSize: 20 }} />, to: 'cases',
    },
    {
      id: 'whatsapp', step: 'We message them', name: 'WhatsApp / SMS',
      detail: 'When shall we call you back?', to: 'cases',
      logo: (
        // Both fit the flow's 36px logo square side by side.
        <Stack direction="row" spacing={0.25} alignItems="center">
          <WhatsAppMark size={15} />
          <SmsRounded sx={(t) => ({ fontSize: 15, color: t.tokens.brand.primary })} />
        </Stack>
      ),
    },
    {
      id: 'calendar', step: 'The callback is booked', name: 'AgencyZoom',
      detail: "On an agent's calendar", logo: <AgencyZoomMark />, to: 'calls',
    },
  ];

  const trend = (points: McDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  const columns: ListColumn<McCaseCard>[] = [
    {
      key: 'caller', header: 'Caller', grow: 1, minWidth: 150,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.caller_name || c.caller}</Typography>
      ),
    },
    {
      key: 'called', header: 'Called', width: 130,
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{fromNow(c.called_at)}</Typography>
      ),
    },
    {
      key: 'status', header: 'Where it stands', grow: 1.4, minWidth: 200,
      render: (c) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <StatusChip {...statusChip(c.status)} />
          {c.status === 'callback_booked' && c.window ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{c.window}</Typography>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <AgentFlow nodes={nodes} loading={loading}
        subtitle="From a call nobody could take to a callback on an agent's calendar"
        onOpen={(to) => navigate(`/agents/missed_call/${to}`)} />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard loading={loading} label="Missed calls" value={whole(totals.missed)}
          info="Calls nobody on the team picked up. Each one got a WhatsApp message asking when to call back."
          trend={trend(data?.missed_by_day ?? [], 'Missed calls by day, this week')} />
        <MetricCard loading={loading} label="Clients who replied" value={whole(totals.replied)}
          info="Missed calls where the client wrote back on WhatsApp." />
        <MetricCard loading={loading} label="Callbacks booked" value={whole(totals.booked)}
          info="Clients who picked a time. Each one is on an agent's AgencyZoom calendar."
          trend={trend(data?.booked_by_day ?? [], 'Callbacks booked by day, this week')} />
        <MetricCard loading={loading} label="Needed a person" value={whole(totals.needs_person)}
          info="The client asked about something else, or a message could not reach them. Each one has its reason." />
        <MetricCard loading={loading} label="Taken by the team" value={whole(totals.taken_by_team)}
          info="Calls someone on the team picked up and pressed 1 for. Nothing more was needed." />
      </Box>

      <Panel title="Latest missed calls" subtitle="Open one to see every message and the callback"
        loading={loading} loadingHeight={160}>
        <ListTable columns={columns} rows={data?.latest ?? []} getRowKey={(c) => c.id} loading={loading}
          onRowClick={(c) => setOpen(c.id)}
          emptyMessage="No missed calls yet. When a call goes unanswered, it shows here within seconds." />
      </Panel>
      {open ? <CaseStoryModal caseId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
